import { useCallback, useEffect, useState } from "react";
import ProductsService from "../api/services/products.service";
import { BasketDeliveryTypes } from "../api/types/products.types";
import { useAuth } from "../context/AuthContext";

/**
 * Delivery charge for the current basket, priced by the backend.
 *
 * The fee and the free-delivery threshold are admin settings stored in USD and
 * converted server-side into the currency being shopped in, so the basket never
 * computes them itself — checkout validates against the same numbers.
 *
 * `refresh` is exposed because the basket contents change on the page and the
 * quote has to follow: crossing the threshold flips the fee to zero.
 */
export const useBasketDelivery = (currencyId: number | null) => {
  const { isLoggedIn } = useAuth();
  const [delivery, setDelivery] = useState<BasketDeliveryTypes | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    // The endpoint reads the signed-in user's basket, so there is nothing to
    // ask for while logged out.
    if (!isLoggedIn || !currencyId) {
      setDelivery(null);
      return;
    }

    setLoading(true);
    try {
      const res = await ProductsService.getBasketDelivery(Number(currencyId));
      setDelivery(res.data);
    } catch (error) {
      // Delivery is additive information; a failure here must not take the
      // basket down with it.
      console.error("Could not load delivery cost:", error);
      setDelivery(null);
    } finally {
      setLoading(false);
    }
  }, [currencyId, isLoggedIn]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { delivery, loading, refresh };
};
